import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/* ── Estilos ── */
const Page = styled.div`
  background: #FFFDF7;
  min-height: 100vh;
  padding-top: 100px;
  padding-bottom: 96px;
`;

const Container = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Eyebrow = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: #C07020;
  text-transform: uppercase;
  margin: 0 0 14px;
`;

const PageTitle = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 600;
  color: #1A1208;
  margin: 0 0 8px;
`;

const LastUpdated = styled.p`
  font-size: 0.82rem;
  color: rgba(26,18,8,0.4);
  margin: 0 0 48px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(26,18,8,0.08);
  margin: 40px 0;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const H2 = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #1A1208;
  margin: 0 0 14px;
`;

const H3 = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1A1208;
  margin: 24px 0 10px;
`;

const P = styled.p`
  font-size: 0.92rem;
  color: rgba(26,18,8,0.7);
  line-height: 1.85;
  margin: 0 0 14px;
`;

const Ul = styled.ul`
  margin: 0 0 14px 20px;
  padding: 0;
  li {
    font-size: 0.92rem;
    color: rgba(26,18,8,0.7);
    line-height: 1.85;
    margin-bottom: 6px;
  }
`;

const A = styled.a`
  color: #7C3A00;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover { color: #C07020; }
`;

const TocBox = styled.div`
  background: #F7F4F0;
  border-radius: 14px;
  padding: 24px 28px;
  margin-bottom: 48px;
`;

const TocTitle = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: rgba(26,18,8,0.4);
  text-transform: uppercase;
  margin: 0 0 14px;
`;

const TocList = styled.ol`
  margin: 0;
  padding-left: 20px;
  li {
    font-size: 0.88rem;
    color: rgba(26,18,8,0.55);
    line-height: 1.9;
    a {
      color: #7C3A00;
      text-decoration: none;
      &:hover { text-decoration: underline; }
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: rgba(26,18,8,0.45);
  text-decoration: none;
  margin-bottom: 40px;
  transition: color 0.2s;
  &:hover { color: #7C3A00; }
`;

/* ── Índice de secciones ── */
const tocItems = [
  { id: 'agreement',       en: 'Agreement to Our Legal Terms',              es: 'Aceptación de nuestros términos legales' },
  { id: 'services',        en: 'Our Services',                               es: 'Nuestros servicios' },
  { id: 'ip',              en: 'Intellectual Property Rights',               es: 'Derechos de propiedad intelectual' },
  { id: 'userreps',        en: 'User Representations',                       es: 'Declaraciones del usuario' },
  { id: 'products',        en: 'Products',                                   es: 'Productos' },
  { id: 'purchases',       en: 'Purchases and Payment',                      es: 'Compras y pago' },
  { id: 'returnno',        en: 'Return Policy',                              es: 'Política de devoluciones' },
  { id: 'prohibited',      en: 'Prohibited Activities',                      es: 'Actividades prohibidas' },
  { id: 'ugc',             en: 'User Generated Contributions',               es: 'Contribuciones del usuario' },
  { id: 'license',         en: 'Contribution License',                       es: 'Licencia de contribución' },
  { id: 'sitemanage',      en: 'Services Management',                        es: 'Gestión de los servicios' },
  { id: 'privacy',         en: 'Privacy Policy',                             es: 'Política de privacidad' },
  { id: 'termination',     en: 'Term and Termination',                       es: 'Vigencia y terminación' },
  { id: 'modifications',   en: 'Modifications and Interruptions',            es: 'Modificaciones e interrupciones' },
  { id: 'law',             en: 'Governing Law',                              es: 'Ley aplicable' },
  { id: 'disputes',        en: 'Dispute Resolution',                         es: 'Resolución de disputas' },
  { id: 'corrections',     en: 'Corrections',                                es: 'Correcciones' },
  { id: 'disclaimer',      en: 'Disclaimer',                                 es: 'Descargo de responsabilidad' },
  { id: 'liability',       en: 'Limitations of Liability',                   es: 'Limitación de responsabilidad' },
  { id: 'indemnification', en: 'Indemnification',                            es: 'Indemnización' },
  { id: 'userdata',        en: 'User Data',                                  es: 'Datos del usuario' },
  { id: 'electronic',      en: 'Electronic Communications',                  es: 'Comunicaciones electrónicas' },
  { id: 'california',      en: 'California Users and Residents',             es: 'Usuarios y residentes de California' },
  { id: 'misc',            en: 'Miscellaneous',                              es: 'Disposiciones generales' },
  { id: 'contact',         en: 'Contact Us',                                 es: 'Contáctanos' },
];

export default function TermsPage() {
  const { t } = useLanguage();

  return (
    <Page>
      <Container>
        <BackLink to="/">← {t('Back to home', 'Volver al inicio')}</BackLink>

        <Eyebrow>{t('Legal', 'Legal')}</Eyebrow>
        <PageTitle>{t('Terms and Conditions', 'Términos y Condiciones')}</PageTitle>
        <LastUpdated>{t('Last updated: April 24, 2026', 'Última actualización: 24 de abril de 2026')}</LastUpdated>

        {/* Tabla de contenido */}
        <TocBox>
          <TocTitle>{t('Table of Contents', 'Tabla de contenido')}</TocTitle>
          <TocList>
            {tocItems.map((item, i) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{i + 1}. {t(item.en, item.es)}</a>
              </li>
            ))}
          </TocList>
        </TocBox>

        <Divider />

        {/* 1. Agreement */}
        <Section id="agreement">
          <H2>1. {t('Agreement to Our Legal Terms', 'Aceptación de nuestros términos legales')}</H2>
          <P>
            {t(
              'We are ZEDYM Group LLC, doing business as Grupo Hacienda Real ("Company," "we," "us," "our"), a company registered in Mexico at Calle Reforma No. 71, San Juan De Los Lagos, 47000.',
              'Somos ZEDYM Group LLC, operando bajo el nombre Grupo Hacienda Real ("Empresa", "nosotros", "nos", "nuestro"), una empresa registrada en México en Calle Reforma No. 71, San Juan De Los Lagos, 47000.'
            )}
          </P>
          <P>
            {t(
              'We operate the website https://grupohaciendareal.com (the "Site"), as well as any other related products and services that refer or link to these legal terms (collectively, the "Services").',
              'Operamos el sitio web https://grupohaciendareal.com (el "Sitio"), así como cualquier otro producto y servicio relacionado que haga referencia o enlace a estos términos legales (colectivamente, los "Servicios").'
            )}
          </P>
          <P>
            {t(
              'You can contact us by email at cajetahaciendareal@gmail.com or by mail to Calle Reforma No. 71, San Juan De Los Lagos, 47000, Mexico.',
              'Puedes contactarnos por correo electrónico en cajetahaciendareal@gmail.com o por correo postal en Calle Reforma No. 71, San Juan De Los Lagos, 47000, México.'
            )}
          </P>
          <P>
            {t(
              'These Legal Terms constitute a legally binding agreement between you and ZEDYM Group LLC concerning your access to and use of the Services. By accessing the Services, you confirm that you have read, understood, and agreed to be bound by all of these Legal Terms. IF YOU DO NOT AGREE, YOU MUST DISCONTINUE USE IMMEDIATELY.',
              'Estos Términos Legales constituyen un acuerdo legalmente vinculante entre usted y ZEDYM Group LLC sobre el acceso y uso de los Servicios. Al acceder a los Servicios, confirma que ha leído, entendido y aceptado estar sujeto a todos estos Términos Legales. SI NO ESTÁ DE ACUERDO, DEBE DEJAR DE USAR LOS SERVICIOS DE INMEDIATO.'
            )}
          </P>
          <P>
            {t(
              'We reserve the right to make changes to these Legal Terms at any time. We will alert you by updating the "Last updated" date. It is your responsibility to review these Legal Terms periodically.',
              'Nos reservamos el derecho de realizar cambios a estos Términos Legales en cualquier momento. Le notificaremos actualizando la fecha de "Última actualización". Es su responsabilidad revisar estos Términos periódicamente.'
            )}
          </P>
          <P>
            {t(
              'The Services are intended for users who are at least 18 years old.',
              'Los Servicios están destinados a usuarios mayores de 18 años.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 2. Services */}
        <Section id="services">
          <H2>2. {t('Our Services', 'Nuestros servicios')}</H2>
          <P>
            {t(
              'The information provided when using the Services is not intended for distribution to any person in any jurisdiction where such distribution would be contrary to law or would subject us to any registration requirement.',
              'La información proporcionada al utilizar los Servicios no está destinada a distribuirse a ninguna persona en ninguna jurisdicción donde dicha distribución sea contraria a la ley o nos someta a algún requisito de registro.'
            )}
          </P>
          <P>
            {t(
              'The Services are not tailored to comply with industry-specific regulations (HIPAA, FISMA, etc.), so if your interactions would be subjected to such laws, you may not use the Services.',
              'Los Servicios no están diseñados para cumplir con regulaciones específicas de la industria (HIPAA, FISMA, etc.), por lo que si sus interacciones están sujetas a dichas leyes, no debe utilizar los Servicios.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 3. IP */}
        <Section id="ip">
          <H2>3. {t('Intellectual Property Rights', 'Derechos de propiedad intelectual')}</H2>
          <H3>{t('Our intellectual property', 'Nuestra propiedad intelectual')}</H3>
          <P>
            {t(
              'We are the owner or licensee of all intellectual property rights in our Services, including all source code, databases, software, website designs, text, photographs, and graphics, as well as the trademarks, service marks, and logos contained therein.',
              'Somos el propietario o licenciatario de todos los derechos de propiedad intelectual en nuestros Servicios, incluyendo todo el código fuente, bases de datos, software, diseños de sitios web, textos, fotografías y gráficos, así como las marcas comerciales, marcas de servicio y logotipos incluidos en ellos.'
            )}
          </P>
          <P>
            {t(
              'Our Content and Marks are protected by copyright and trademark laws. They are provided for your personal, non-commercial use only.',
              'Nuestro Contenido y Marcas están protegidos por las leyes de derechos de autor y marcas registradas. Se proporcionan únicamente para su uso personal y no comercial.'
            )}
          </P>
          <H3>{t('Your use of our Services', 'Su uso de nuestros Servicios')}</H3>
          <P>
            {t(
              'Subject to your compliance with these Legal Terms, we grant you a non-exclusive, non-transferable, revocable license to access the Services and download or print a copy of any portion of the Content solely for your personal, non-commercial use.',
              'Sujeto a su cumplimiento de estos Términos Legales, le otorgamos una licencia no exclusiva, intransferible y revocable para acceder a los Servicios y descargar o imprimir una copia de cualquier parte del Contenido únicamente para su uso personal y no comercial.'
            )}
          </P>
          <P>
            {t(
              'No part of the Services and no Content or Marks may be copied, reproduced, published, uploaded, distributed, sold, or otherwise exploited for any commercial purpose without our express prior written permission.',
              'Ninguna parte de los Servicios ni ningún Contenido o Marca puede ser copiado, reproducido, publicado, subido, distribuido, vendido o explotado de otra forma con fines comerciales sin nuestro permiso previo expreso por escrito.'
            )}
          </P>
          <H3>{t('Your submissions', 'Sus envíos')}</H3>
          <P>
            {t(
              'By sending us any question, comment, suggestion, idea, feedback, or other information about the Services, you agree to assign to us all intellectual property rights in such submission.',
              'Al enviarnos cualquier pregunta, comentario, sugerencia, idea, comentario u otra información sobre los Servicios, acepta cedernos todos los derechos de propiedad intelectual sobre dicho envío.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 4. User reps */}
        <Section id="userreps">
          <H2>4. {t('User Representations', 'Declaraciones del usuario')}</H2>
          <P>{t('By using the Services, you represent and warrant that:', 'Al utilizar los Servicios, usted declara y garantiza que:')}</P>
          <Ul>
            <li>{t('You have the legal capacity and agree to comply with these Legal Terms;', 'Tiene la capacidad legal y acepta cumplir con estos Términos Legales;')}</li>
            <li>{t('You are not a minor in the jurisdiction in which you reside;', 'No es menor de edad en la jurisdicción en la que reside;')}</li>
            <li>{t('You will not access the Services through automated or non-human means;', 'No accederá a los Servicios mediante medios automatizados o no humanos;')}</li>
            <li>{t('You will not use the Services for any illegal or unauthorized purpose;', 'No utilizará los Servicios para ningún propósito ilegal o no autorizado;')}</li>
            <li>{t('Your use of the Services will not violate any applicable law or regulation.', 'Su uso de los Servicios no violará ninguna ley o regulación aplicable.')}</li>
          </Ul>
        </Section>

        <Divider />

        {/* 5. Products */}
        <Section id="products">
          <H2>5. {t('Products', 'Productos')}</H2>
          <P>
            {t(
              'We make every effort to display as accurately as possible the colors, features, specifications, and details of the products available on the Services. However, we do not guarantee that the colors, features, specifications, and details will be accurate or complete.',
              'Hacemos todo lo posible por mostrar con la mayor precisión posible los colores, características, especificaciones y detalles de los productos disponibles en los Servicios. Sin embargo, no garantizamos que los colores, características, especificaciones y detalles sean exactos o completos.'
            )}
          </P>
          <P>
            {t(
              'All products are subject to availability. We reserve the right to discontinue any products at any time. Prices for all products are subject to change.',
              'Todos los productos están sujetos a disponibilidad. Nos reservamos el derecho de descontinuar cualquier producto en cualquier momento. Los precios de todos los productos están sujetos a cambios.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 6. Purchases */}
        <Section id="purchases">
          <H2>6. {t('Purchases and Payment', 'Compras y pago')}</H2>
          <P>{t('We accept the following forms of payment:', 'Aceptamos las siguientes formas de pago:')}</P>
          <Ul>
            <li>Visa</li>
            <li>Mastercard</li>
            <li>American Express</li>
            <li>Discover</li>
          </Ul>
          <P>
            {t(
              'You agree to provide current, complete, and accurate purchase and account information for all purchases. Sales tax will be added to the price of purchases as deemed required by us. We may change prices at any time. All payments shall be in US dollars.',
              'Acepta proporcionar información de compra y cuenta actual, completa y precisa para todas las compras. El impuesto sobre las ventas se añadirá al precio de las compras según sea necesario. Podemos cambiar los precios en cualquier momento. Todos los pagos se realizarán en dólares estadounidenses.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 7. Return Policy */}
        <Section id="returnno">
          <H2>7. {t('Return Policy', 'Política de devoluciones')}</H2>
          <H3>{t('No returns or refunds on food products', 'Sin devoluciones ni reembolsos en productos alimenticios')}</H3>
          <P>
            {t(
              'Due to the perishable and food-safety nature of our products, ALL SALES ARE FINAL. We do not accept returns, exchanges, or issue refunds under any circumstances, except as described below.',
              'Debido a la naturaleza perecedera y de seguridad alimentaria de nuestros productos, TODAS LAS VENTAS SON DEFINITIVAS. No aceptamos devoluciones, cambios ni emitimos reembolsos bajo ninguna circunstancia, excepto lo descrito a continuación.'
            )}
          </P>
          <P>
            {t(
              'This policy applies regardless of whether the product has been opened or not. Once an order is confirmed and payment is processed, it cannot be cancelled.',
              'Esta política aplica independientemente de si el producto ha sido abierto o no. Una vez que un pedido es confirmado y el pago procesado, no puede ser cancelado.'
            )}
          </P>
          <H3>{t('Exception: damaged or defective products', 'Excepción: productos dañados o defectuosos')}</H3>
          <P>
            {t(
              'The only exception to our no-return policy is if you receive a product that is visibly damaged, defective, or significantly different from what was ordered. In such cases:',
              'La única excepción a nuestra política de no devoluciones es si recibes un producto visiblemente dañado, defectuoso o significativamente diferente a lo pedido. En ese caso:'
            )}
          </P>
          <Ul>
            <li>{t('You must contact us within 48 hours of delivery at cajetahaciendareal@gmail.com.', 'Debes contactarnos dentro de las 48 horas posteriores a la entrega en cajetahaciendareal@gmail.com.')}</li>
            <li>{t('You must include clear photographic evidence of the damage or defect.', 'Debes incluir evidencia fotográfica clara del daño o defecto.')}</li>
            <li>{t('We will evaluate each case individually and, at our sole discretion, may offer a replacement or store credit.', 'Evaluaremos cada caso individualmente y, a nuestra entera discreción, podremos ofrecer un reemplazo o crédito en tienda.')}</li>
            <li>{t('Cash refunds will not be issued even in cases of damage.', 'No se emitirán reembolsos en efectivo ni siquiera en casos de daño.')}</li>
          </Ul>
          <H3>{t('Incorrect delivery address', 'Dirección de entrega incorrecta')}</H3>
          <P>
            {t(
              'The customer is solely responsible for providing a complete and accurate delivery address. Orders that are undeliverable or lost due to an incorrect address provided by the customer will not be refunded or reshipped at our expense.',
              'El cliente es el único responsable de proporcionar una dirección de entrega completa y correcta. Los pedidos que no puedan entregarse o se pierdan debido a una dirección incorrecta proporcionada por el cliente no serán reembolsados ni reenviados a nuestro costo.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 8. Prohibited */}
        <Section id="prohibited">
          <H2>8. {t('Prohibited Activities', 'Actividades prohibidas')}</H2>
          <P>{t('You may not use the Services for any purpose other than that for which we make the Services available. As a user, you agree not to:', 'No puede utilizar los Servicios para ningún propósito distinto al que los ponemos a disposición. Como usuario, acepta no:')}</P>
          <Ul>
            <li>{t('Systematically retrieve data to create a collection or database without written permission.', 'Recuperar datos de forma sistemática para crear una colección o base de datos sin permiso escrito.')}</li>
            <li>{t('Trick, defraud, or mislead us or other users to obtain sensitive account information.', 'Engañar, defraudar o inducir a error a nosotros o a otros usuarios para obtener información sensible de la cuenta.')}</li>
            <li>{t('Circumvent, disable, or otherwise interfere with security-related features of the Services.', 'Eludir, deshabilitar o interferir de otro modo con las funciones de seguridad de los Servicios.')}</li>
            <li>{t('Use the Services in a manner inconsistent with any applicable laws or regulations.', 'Utilizar los Servicios de manera incompatible con cualquier ley o regulación aplicable.')}</li>
            <li>{t('Upload or transmit viruses, Trojan horses, or other material that interferes with the use of the Services.', 'Cargar o transmitir virus, troyanos u otro material que interfiera con el uso de los Servicios.')}</li>
            <li>{t('Engage in any automated use of the system, such as scripts, data mining, robots, or similar tools.', 'Realizar cualquier uso automatizado del sistema, como scripts, minería de datos, robots o herramientas similares.')}</li>
            <li>{t('Use the Services as part of any effort to compete with us or for any revenue-generating endeavor.', 'Utilizar los Servicios como parte de cualquier esfuerzo para competir con nosotros o para cualquier actividad generadora de ingresos.')}</li>
          </Ul>
        </Section>

        <Divider />

        {/* 9. UGC */}
        <Section id="ugc">
          <H2>9. {t('User Generated Contributions', 'Contribuciones del usuario')}</H2>
          <P>
            {t(
              'The Services do not currently offer users the ability to submit or post content. We may provide such opportunities in the future, and any Contributions you transmit may be treated in accordance with the Privacy Policy.',
              'Actualmente, los Servicios no ofrecen a los usuarios la posibilidad de enviar o publicar contenido. Podremos ofrecer dicha posibilidad en el futuro, y cualquier Contribución que transmita podrá tratarse de acuerdo con la Política de Privacidad.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 10. License */}
        <Section id="license">
          <H2>10. {t('Contribution License', 'Licencia de contribución')}</H2>
          <P>
            {t(
              'By submitting suggestions or other feedback regarding the Services, you agree that we can use and share such feedback for any purpose without compensation to you.',
              'Al enviar sugerencias u otros comentarios sobre los Servicios, acepta que podemos usar y compartir dichos comentarios para cualquier propósito sin compensación para usted.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 11. Site manage */}
        <Section id="sitemanage">
          <H2>11. {t('Services Management', 'Gestión de los servicios')}</H2>
          <P>{t('We reserve the right, but not the obligation, to:', 'Nos reservamos el derecho, pero no la obligación, de:')}</P>
          <Ul>
            <li>{t('Monitor the Services for violations of these Legal Terms;', 'Supervisar los Servicios para detectar infracciones de estos Términos Legales;')}</li>
            <li>{t('Take appropriate legal action against anyone who violates the law or these Legal Terms;', 'Tomar las medidas legales adecuadas contra cualquier persona que viole la ley o estos Términos Legales;')}</li>
            <li>{t('Refuse, restrict, or disable any of your Contributions or any portion thereof;', 'Rechazar, restringir o deshabilitar cualquiera de sus Contribuciones o parte de ellas;')}</li>
            <li>{t('Otherwise manage the Services to protect our rights and property.', 'Gestionar los Servicios de otra manera para proteger nuestros derechos y propiedad.')}</li>
          </Ul>
        </Section>

        <Divider />

        {/* 12. Privacy */}
        <Section id="privacy">
          <H2>12. {t('Privacy Policy', 'Política de privacidad')}</H2>
          <P>
            {t(
              'We care about data privacy and security. By using the Services, you agree to be bound by our Privacy Policy. Please be advised the Services are hosted in the United States. If you access the Services from any other region, through your continued use you are transferring your data to the United States.',
              'Nos preocupamos por la privacidad y seguridad de los datos. Al usar los Servicios, acepta nuestra Política de Privacidad. Tenga en cuenta que los Servicios están alojados en los Estados Unidos. Si accede a los Servicios desde otra región, al continuar usándolos está transfiriendo sus datos a los Estados Unidos.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 13. Termination */}
        <Section id="termination">
          <H2>13. {t('Term and Termination', 'Vigencia y terminación')}</H2>
          <P>
            {t(
              'These Legal Terms shall remain in full force and effect while you use the Services. We reserve the right to deny access to and use of the Services to any person for any reason, including breach of any representation, warranty, or covenant contained in these Legal Terms.',
              'Estos Términos Legales permanecerán en plena vigencia mientras utilice los Servicios. Nos reservamos el derecho de denegar el acceso y uso de los Servicios a cualquier persona por cualquier motivo, incluyendo el incumplimiento de cualquier declaración, garantía o acuerdo contenido en estos Términos Legales.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 14. Modifications */}
        <Section id="modifications">
          <H2>14. {t('Modifications and Interruptions', 'Modificaciones e interrupciones')}</H2>
          <P>
            {t(
              'We reserve the right to change, modify, or remove the contents of the Services at any time without notice. We also reserve the right to modify or discontinue all or part of the Services without notice at any time.',
              'Nos reservamos el derecho de cambiar, modificar o eliminar el contenido de los Servicios en cualquier momento sin previo aviso. También nos reservamos el derecho de modificar o discontinuar total o parcialmente los Servicios en cualquier momento sin previo aviso.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 15. Law */}
        <Section id="law">
          <H2>15. {t('Governing Law', 'Ley aplicable')}</H2>
          <P>
            {t(
              'These Legal Terms shall be governed by and defined following the laws of Mexico. ZEDYM Group LLC and yourself irrevocably consent that the courts of Mexico shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Legal Terms.',
              'Estos Términos Legales se regirán e interpretarán de conformidad con las leyes de México. ZEDYM Group LLC y usted aceptan irrevocablemente que los tribunales de México tendrán jurisdicción exclusiva para resolver cualquier disputa relacionada con estos Términos Legales.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 16. Disputes */}
        <Section id="disputes">
          <H2>16. {t('Dispute Resolution', 'Resolución de disputas')}</H2>
          <H3>{t('Informal Negotiations', 'Negociaciones informales')}</H3>
          <P>
            {t(
              'To expedite resolution and control the cost of any dispute, the Parties agree to first attempt to negotiate any dispute informally for at least 30 days before initiating arbitration.',
              'Para agilizar la resolución y controlar el costo de cualquier disputa, las Partes acuerdan intentar primero negociar cualquier disputa de manera informal durante al menos 30 días antes de iniciar el arbitraje.'
            )}
          </P>
          <H3>{t('Binding Arbitration', 'Arbitraje vinculante')}</H3>
          <P>
            {t(
              'Any dispute arising out of or in connection with these Legal Terms shall be referred to and finally resolved by arbitration. The seat of arbitration shall be San Juan De Los Lagos, Mexico. The language of the proceedings shall be Spanish.',
              'Cualquier disputa que surja de o en relación con estos Términos Legales será remitida y resuelta definitivamente mediante arbitraje. La sede del arbitraje será San Juan De Los Lagos, México. El idioma del procedimiento será el español.'
            )}
          </P>
          <H3>{t('Restrictions', 'Restricciones')}</H3>
          <P>
            {t(
              'The Parties agree that any arbitration shall be limited to the dispute between the Parties individually. No arbitration shall be joined with any other proceeding, and there is no right to arbitrate on a class-action basis.',
              'Las Partes acuerdan que cualquier arbitraje se limitará a la disputa entre las Partes de manera individual. Ningún arbitraje se combinará con ningún otro procedimiento, y no existe el derecho a arbitrar sobre una base de acción colectiva.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 17. Corrections */}
        <Section id="corrections">
          <H2>17. {t('Corrections', 'Correcciones')}</H2>
          <P>
            {t(
              'There may be information on the Services that contains typographical errors, inaccuracies, or omissions. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Services at any time, without prior notice.',
              'Puede haber información en los Servicios que contenga errores tipográficos, inexactitudes u omisiones. Nos reservamos el derecho de corregir cualquier error, inexactitud u omisión y de cambiar o actualizar la información en los Servicios en cualquier momento, sin previo aviso.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 18. Disclaimer */}
        <Section id="disclaimer">
          <H2>18. {t('Disclaimer', 'Descargo de responsabilidad')}</H2>
          <P>
            {t(
              'THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
              'LOS SERVICIOS SE PROPORCIONAN "TAL COMO ESTÁN" Y "SEGÚN DISPONIBILIDAD". EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, RENUNCIAMOS A TODAS LAS GARANTÍAS, EXPRESAS O IMPLÍCITAS, INCLUYENDO LAS GARANTÍAS IMPLÍCITAS DE COMERCIABILIDAD, IDONEIDAD PARA UN PROPÓSITO PARTICULAR Y NO INFRACCIÓN.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 19. Liability */}
        <Section id="liability">
          <H2>19. {t('Limitations of Liability', 'Limitación de responsabilidad')}</H2>
          <P>
            {t(
              'IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES.',
              'EN NINGÚN CASO NOSOTROS NI NUESTROS DIRECTORES, EMPLEADOS O AGENTES SEREMOS RESPONSABLES ANTE USTED O CUALQUIER TERCERO POR DAÑOS DIRECTOS, INDIRECTOS, CONSECUENTES, EJEMPLARES, INCIDENTALES, ESPECIALES O PUNITIVOS QUE SURJAN DE SU USO DE LOS SERVICIOS.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 20. Indemnification */}
        <Section id="indemnification">
          <H2>20. {t('Indemnification', 'Indemnización')}</H2>
          <P>
            {t(
              'You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand arising out of: (1) your Contributions; (2) use of the Services; (3) breach of these Legal Terms; (4) any breach of your representations and warranties; or (5) your violation of the rights of a third party.',
              'Acepta defendernos, indemnizarnos y eximirnos de responsabilidad, incluidas nuestras subsidiarias, afiliadas, funcionarios, agentes, socios y empleados, de y contra cualquier pérdida, daño, responsabilidad, reclamación o demanda que surja de: (1) sus Contribuciones; (2) el uso de los Servicios; (3) el incumplimiento de estos Términos Legales; (4) el incumplimiento de sus declaraciones y garantías; o (5) la violación de los derechos de un tercero.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 21. User data */}
        <Section id="userdata">
          <H2>21. {t('User Data', 'Datos del usuario')}</H2>
          <P>
            {t(
              'We will maintain certain data that you transmit to the Services for the purpose of managing the performance of the Services. Although we perform regular routine backups, you are solely responsible for all data that you transmit or that relates to any activity you have undertaken using the Services.',
              'Mantendremos ciertos datos que transmita a los Servicios con el propósito de gestionar el rendimiento de los mismos. Aunque realizamos copias de seguridad de rutina, usted es el único responsable de todos los datos que transmita o que estén relacionados con cualquier actividad que haya realizado utilizando los Servicios.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 22. Electronic */}
        <Section id="electronic">
          <H2>22. {t('Electronic Communications, Transactions, and Signatures', 'Comunicaciones, transacciones y firmas electrónicas')}</H2>
          <P>
            {t(
              'Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications provided to you electronically satisfy any legal requirement that such communication be in writing.',
              'Visitar los Servicios, enviarnos correos electrónicos y completar formularios en línea constituyen comunicaciones electrónicas. Usted consiente recibir comunicaciones electrónicas y acepta que todos los acuerdos, avisos, divulgaciones y otras comunicaciones que le proporcionamos electrónicamente satisfacen cualquier requisito legal de que dicha comunicación sea por escrito.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 23. California */}
        <Section id="california">
          <H2>23. {t('California Users and Residents', 'Usuarios y residentes de California')}</H2>
          <P>
            {t(
              'If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at (800) 952-5210.',
              'Si alguna queja presentada ante nosotros no se resuelve de manera satisfactoria, puede comunicarse con la Unidad de Asistencia de Quejas de la División de Servicios al Consumidor del Departamento de Asuntos del Consumidor de California por escrito a 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 o por teléfono al (800) 952-5210.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 24. Misc */}
        <Section id="misc">
          <H2>24. {t('Miscellaneous', 'Disposiciones generales')}</H2>
          <P>
            {t(
              'These Legal Terms and any policies or operating rules posted by us on the Services constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Legal Terms shall not operate as a waiver of such right or provision.',
              'Estos Términos Legales y cualquier política o regla operativa publicada por nosotros en los Servicios constituyen el acuerdo y entendimiento completo entre usted y nosotros. Nuestra falta de ejercicio o exigencia de cualquier derecho o disposición de estos Términos Legales no operará como una renuncia a dicho derecho o disposición.'
            )}
          </P>
          <P>
            {t(
              'If any provision or part of a provision of these Legal Terms is determined to be unlawful, void, or unenforceable, that provision is deemed severable from these Legal Terms and does not affect the validity and enforceability of any remaining provisions.',
              'Si alguna disposición o parte de una disposición de estos Términos Legales se determina como ilegal, nula o inaplicable, dicha disposición se considera separable de estos Términos Legales y no afecta la validez y aplicabilidad de las disposiciones restantes.'
            )}
          </P>
        </Section>

        <Divider />

        {/* 25. Contact */}
        <Section id="contact">
          <H2>25. {t('Contact Us', 'Contáctanos')}</H2>
          <P>{t('In order to resolve a complaint or receive further information, please contact us at:', 'Para resolver una queja o recibir más información, comuníquese con nosotros en:')}</P>
          <P>
            <strong>ZEDYM Group LLC / Grupo Hacienda Real</strong><br />
            Calle Reforma No. 71<br />
            San Juan De Los Lagos, 47000<br />
            {t('Mexico', 'México')}<br />
            <A href="mailto:cajetahaciendareal@gmail.com">cajetahaciendareal@gmail.com</A>
          </P>
        </Section>

      </Container>
    </Page>
  );
}
